/**
 * features/tax-invoices/TaxInvoiceDetailPage.tsx
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Pencil, Trash2 } from 'lucide-react';
import { formatDate, formatMoney } from '@ft/core';
import { ApiError, resolveApiUrl } from '~/lib/api';
import { PageHeader } from '~/ui/layout';
import { Alert, Button, StatusBadge } from '~/ui/primitives';
import { ConfirmDialog, useToast } from '~/ui/overlays';
import { DocumentPreviewFrame } from '~/features/documents/DocumentPreviewFrame';
import { useDeleteTaxInvoice, useTaxInvoice } from './hooks';

export function TaxInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: invoice, isLoading } = useTaxInvoice(id);
  const deleteInvoice = useDeleteTaxInvoice();
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (isLoading || !invoice) {
    return <p className="text-sm text-fg-muted">กำลังโหลด…</p>;
  }

  async function handleDelete() {
    if (!id) return;
    try {
      await deleteInvoice.mutateAsync(id);
      toast.success('ลบใบกำกับภาษีแล้ว ใบเสนอราคาต้นทาง (ถ้ามี) กลับเป็นแบบร่างแล้ว');
      navigate('/tax-invoices');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'ลบไม่สำเร็จ');
      setDeleteOpen(false);
    }
  }

  const editable = invoice.status !== 'cancelled';

  return (
    <div>
      <PageHeader
        title={invoice.documentNumber}
        breadcrumb={
          <button onClick={() => navigate('/tax-invoices')} className="inline-flex items-center gap-1 hover:text-fg">
            <ArrowLeft size={13} />
            ใบเสร็จ/ใบกำกับภาษี
          </button>
        }
        actions={
          <>
            <a
              href={resolveApiUrl(`/api/tax-invoices/${id}/pdf?download=1`, true)}
              className="btn btn-secondary"
              target="_blank"
              rel="noreferrer"
            >
              <Download size={15} />
              ดาวน์โหลด PDF
            </a>
            {editable ? (
              <Button variant="secondary" onClick={() => navigate(`/tax-invoices/${id}/edit`)}>
                <Pencil size={15} />
                แก้ไข
              </Button>
            ) : null}
            <Button variant="danger" iconOnly onClick={() => setDeleteOpen(true)} aria-label="ลบ">
              <Trash2 size={15} />
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <StatusBadge status={invoice.status} />
        <span className="text-sm text-fg-muted">ลูกค้า: {invoice.customerName}</span>
        <span className="text-sm text-fg-muted">วันที่: {formatDate(invoice.date)}</span>
        <span className="tabular text-sm font-medium text-fg">
          ยอดรวม {formatMoney(invoice.totalAmount)} บาท
        </span>
      </div>

      {invoice.sourceQuotation ? (
        <div className="mb-4">
          <Alert tone="info">
            ออกจากใบเสนอราคา{' '}
            <button className="link" onClick={() => navigate(`/quotations/${invoice.sourceQuotation?.id}`)}>
              {invoice.sourceQuotation.documentNumber}
            </button>
          </Alert>
        </div>
      ) : null}

      <DocumentPreviewFrame
        src={`/api/tax-invoices/${id}/preview`}
        title={`ใบกำกับภาษี ${invoice.documentNumber}`}
      />

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => void handleDelete()}
        title="ลบใบเสร็จ/ใบกำกับภาษี"
        message={`ต้องการลบเอกสาร ${invoice.documentNumber} ใช่หรือไม่ ถ้าเอกสารนี้ออกจากใบเสนอราคา สถานะใบเสนอราคาจะกลับเป็นแบบร่าง การลบไม่สามารถย้อนกลับได้`}
        confirmLabel="ลบ"
        loading={deleteInvoice.isPending}
      />
    </div>
  );
}
